import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import translations from '../locales/translations.json';

const STORAGE_KEY = 'apkakhata_locale';
const SUPPORTED_LOCALES = ['en', 'hi', 'pa', 'ta', 'ml'];

const LanguageContext = createContext(null);

function getNested(obj, path) {
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return undefined;
    current = current[key];
  }
  return current;
}

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return SUPPORTED_LOCALES.includes(saved) ? saved : 'en';
    } catch {
      return 'en';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
      const html = document.documentElement;
      html.setAttribute('lang', locale === 'en' ? 'en' : `${locale}-IN`);
    } catch (e) {
      console.warn('LanguageContext: localStorage/lang set failed', e);
    }
  }, [locale]);

  const setLocale = (newLocale) => {
    if (SUPPORTED_LOCALES.includes(newLocale)) setLocaleState(newLocale);
  };

  const t = useMemo(() => {
    const strings = translations[locale] || translations.en;
    return (path) => {
      const value = getNested(strings, path);
      return typeof value === 'string' ? value : getNested(translations.en, path) || path;
    };
  }, [locale]);

  const value = useMemo(
    () => ({ locale, setLocale, t, supportedLocales: SUPPORTED_LOCALES }),
    [locale, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}

export function useTranslation() {
  const { t, locale } = useLanguage();
  return { t, locale };
}
